import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { GoogleLogin } from '@react-oauth/google';
import { 
  handleGoogleCredential,
  completeGoogleRegistration 
} from '../utils/googleAuth';
import GoogleRoleSelectionModal from '../components/GoogleRoleSelectionModal';
import WelcomeRegistrationModal from '../components/WelcomeRegistrationModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthPage = () => {
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  
  // Tab state - check URL for ?mode=register
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('mode') === 'register' ? 'register' : 'login';
  });
  
  // Google OAuth states
  const [showGoogleModal, setShowGoogleModal] = useState(false);
  const [googleTempToken, setGoogleTempToken] = useState(null);
  const [googleUserData, setGoogleUserData] = useState(null);
  
  // Email registration states
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [registrationToken, setRegistrationToken] = useState(null);
  const [registrationEmail, setRegistrationEmail] = useState(null);

  // Login state
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  // Register state (simplified)
  const [registerData, setRegisterData] = useState({
    email: '',
    password: ''
  });

  // Handle Google OAuth success from @react-oauth/google
  const handleGoogleSuccess = async (credentialResponse) => {
    console.log('🔍 Google OAuth success:', credentialResponse);
    setGoogleLoading(true);
    
    try {
      const result = await handleGoogleCredential(credentialResponse.credential);
      console.log('🔍 Backend verification result:', result);
      
      if (result.status === 'success') {
        // Existing user - log them in
        console.log('✅ Existing user login');
        login(result.token, result.user);
        toast.success('Welcome back!');
        navigate('/dashboard');
      } else if (result.status === 'needs_role') {
        // New user - show role selection modal
        console.log('✅ New user - showing role selection');
        setGoogleTempToken(result.temp_token);
        setGoogleUserData({
          email: result.email,
          name: result.name || '',
          givenName: result.given_name || '',
          familyName: result.family_name || ''
        });
        setShowGoogleModal(true);
      }
    } catch (error) {
      console.error('❌ Google auth error:', error);
      toast.error(error.response?.data?.detail || 'Google authentication failed');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    console.error('❌ Google OAuth error');
    toast.error('Google authentication failed. Please try again.');
    setGoogleLoading(false);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginData);
      login(response.data.token, response.data.user);
      toast.success('Welcome back!');
      
      // Redirect admin users to admin dashboard
      if (response.data.user.role === 'super_admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, registerData);
      
      console.log('🔍 Registration response:', response.data);
      
      // Check if profile completion is needed
      if (response.data.needs_profile_completion) {
        console.log('✅ Profile completion needed - showing modal');
        // Save token but DON'T call login() yet - modal will do it after completion
        setRegistrationToken(response.data.token);
        setRegistrationEmail(registerData.email);
        // Store user data temporarily
        localStorage.setItem('token', response.data.token);
        setShowWelcomeModal(true);
        console.log('✅ Modal state set to true, showWelcomeModal:', true);
        // DON'T navigate - stay on this page to show modal
      } else {
        // Shouldn't happen but handle it
        console.log('⚠️ Profile already complete - navigating to dashboard');
        login(response.data.token, response.data.user);
        toast.success('Account created successfully!');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('❌ Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteGoogleRegistration = async (data) => {
    try {
      const result = await completeGoogleRegistration(
        googleTempToken,
        data.first_name,
        data.last_name,
        data.role,
        data.company_name,
        data.city,
        data.country
      );
      
      // Log user in
      login(result.token, result.user);
      toast.success('Account created successfully!');
      setShowGoogleModal(false);
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to complete registration:', error);
      toast.error('Error completing registration');
    }
  };

  const handleCompleteEmailRegistration = async (data) => {
    try {
      const response = await axios.post(
        `${API}/auth/complete-profile`,
        data,
        {
          headers: { Authorization: `Bearer ${registrationToken}` }
        }
      );
      
      // Update user in context
      login(registrationToken, response.data.user);
      toast.success('Profile completed successfully!');
      setShowWelcomeModal(false);
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to complete profile:', error);
      toast.error(error.response?.data?.detail || 'Error completing profile');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md fade-in">
        <Button 
          variant="ghost" 
          className="mb-6 text-gray-700"
          onClick={() => navigate('/')}
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        <div className="flex items-center justify-center mb-8">
          <img 
            src="/snow-exchange-logo.png" 
            alt="Snow Exchange" 
            className="w-12 h-12 rounded-lg mr-3"
          />
          <span className="text-2xl font-bold" style={{ color: '#1B3240' }}>Snow Exchange</span>
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader>
            <CardTitle className="text-2xl" style={{ color: '#1B3240' }}>Welcome</CardTitle>
            <CardDescription>Sign in or create your account</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="your@email.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="login-password">Password</Label>
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="••••••••"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                      data-testid="login-password-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full btn-snow"
                    disabled={loading}
                    data-testid="login-submit-btn"
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                  
                  {/* Divider */}
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">or</span>
                    </div>
                  </div>
                  
                  {/* Google Sign In Button */}
                  <div className="flex justify-center">
                    {googleLoading ? (
                      <Button variant="outline" className="w-full" disabled>
                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                        </svg>
                        Signing in...
                      </Button>
                    ) : (
                      <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                        useOneTap={false}
                        theme="outline"
                        size="large"
                        text="continue_with"
                        width="100%"
                      />
                    )}
                  </div>
                  
                  <div className="text-center mt-3">
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      Forgot your password?
                    </button>
                  </div>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="register-email">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      placeholder="your@email.com"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-password">Password</Label>
                    <Input
                      id="register-password"
                      type="password"
                      placeholder="••••••••"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                      data-testid="register-password-input"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full btn-snow"
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? 'Creating account...' : 'Create Account'}
                  </Button>
                  
                  {/* Divider */}
                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">or</span>
                    </div>
                  </div>
                  
                  {/* Google Sign Up Button */}
                  <div className="flex justify-center">
                    {googleLoading ? (
                      <Button variant="outline" className="w-full" disabled>
                        <svg className="animate-spin h-5 w-5 mr-2" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                        </svg>
                        Creating account...
                      </Button>
                    ) : (
                      <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={handleGoogleError}
                        useOneTap={false}
                        theme="outline"
                        size="large"
                        text="signup_with"
                        width="100%"
                      />
                    )}
                  </div>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
        
        {/* Google Role Selection Modal */}
        <GoogleRoleSelectionModal
          isOpen={showGoogleModal}
          onClose={() => setShowGoogleModal(false)}
          onSubmit={handleCompleteGoogleRegistration}
          userData={googleUserData}
        />
        
        {/* Email Registration Welcome Modal */}
        <WelcomeRegistrationModal
          isOpen={showWelcomeModal}
          onClose={() => {}}  // Don't allow closing - must complete
          onSubmit={handleCompleteEmailRegistration}
          userEmail={registrationEmail}
        />
        
        {/* Loading Overlay for Google Auth */}
        {googleLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 flex flex-col items-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mb-4"></div>
              <p className="text-gray-700">Authenticating...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthPage;
